import React from 'react';
import { Tooltip } from '@mui/material';
import { useCurrency } from '../../context/CurrencyContext';
import InteractiveTooltip from './InteractiveTooltip';

const CurrencyDisplay = ({
  amount,
  currency = null,
  exchangeRate = null,
  showSymbol = true,
  showCode = false,
  compact = false,
  threshold = 10000000,
  className = '',
  style = {},
  ...props
}) => {
  const { formatAmount, formatNumber, formatCompact, baseCurrency, currencies } = useCurrency();

  // Resolve display currency object
  let displayCurrency = currency;
  if (typeof currency === 'string') {
    displayCurrency = currencies?.find(c => c.code === currency || c.id === currency) || { code: currency, symbol: currency };
  } else if (!currency) {
    displayCurrency = baseCurrency;
  }

  const currencyCode = (displayCurrency?.code || baseCurrency?.code || 'USD').toUpperCase();
  const baseCode = (baseCurrency?.code || 'USD').toUpperCase();
  const isBaseCurrency = currencyCode === baseCode;
  const numAmount = parseFloat(amount) || 0;

  // Resolve rate: prefer explicit exchangeRate, then displayCurrency.exchangeRate, then fallback to live currencies rate
  let resolvedRate = parseFloat(exchangeRate ?? displayCurrency?.exchangeRate);
  if ((isNaN(resolvedRate) || resolvedRate <= 0 || (resolvedRate === 1.0 && !isBaseCurrency)) && !isBaseCurrency) {
    const matchedCurr = currencies?.find(c => c.code?.toUpperCase() === currencyCode || c.id === displayCurrency?.id);
    const liveRate = parseFloat(matchedCurr?.exchangeRate);
    if (!isNaN(liveRate) && liveRate > 0) {
      resolvedRate = liveRate;
    }
  }
  const rate = (!isNaN(resolvedRate) && resolvedRate > 0) ? resolvedRate : 1.0;

  // In FluxERP: 1 Base Currency = [rate] Foreign Currency
  // Therefore, foreignAmount / rate = baseEquivalent
  const baseEquivalent = rate > 0 ? (numAmount / rate) : numAmount;
  const baseSymbol = baseCurrency?.symbol || '$';

  if (compact) {
    const { display, full, isCompact } = formatCompact(amount, displayCurrency, threshold);

    if (isCompact) {
      const extraHover = !isBaseCurrency
        ? ` (≈ ${baseSymbol}${baseEquivalent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseCode})`
        : '';
      return (
        <InteractiveTooltip title={`Full value: ${full}${extraHover}`}>
          <span className={`number-ltr ${className}`.trim()} dir="ltr" style={{ unicodeBidi: 'embed', display: 'inline-block', ...style, cursor: 'help' }} {...props}>
            {display}
            {showCode && displayCurrency ? ` (${displayCurrency.code})` : ''}
          </span>
        </InteractiveTooltip>
      );
    }
  }

  // Base rendered content
  let content;
  if (!showSymbol) {
    content = (
      <span className={`number-ltr ${className}`.trim()} dir="ltr" style={{ unicodeBidi: 'embed', display: 'inline-block', ...style }} {...props}>
        {formatNumber(amount)}
        {showCode && displayCurrency ? ` ${displayCurrency.code}` : ''}
      </span>
    );
  } else if (!isBaseCurrency) {
    // Foreign currency display (e.g. PKR 2,500.00)
    const sym = displayCurrency?.symbol || currencyCode;
    content = (
      <span className={`number-ltr ${className}`.trim()} dir="ltr" style={{ unicodeBidi: 'embed', display: 'inline-block', ...style }} {...props}>
        {sym} {numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    );
  } else {
    content = (
      <span className={`number-ltr ${className}`.trim()} dir="ltr" style={{ unicodeBidi: 'embed', display: 'inline-block', ...style }} {...props}>
        {formatAmount(amount, displayCurrency)}
        {showCode && displayCurrency ? ` (${displayCurrency.code})` : ''}
      </span>
    );
  }

  // Construct Hover Tooltip for both Foreign and Base currencies
  let tooltipText = '';
  if (!isBaseCurrency) {
    const rateText = rate && rate > 0 && rate !== 1.0 ? ` • Rate: 1 ${baseCode} = ${rate.toFixed(rate < 1 ? 4 : 2)} ${currencyCode}` : '';
    tooltipText = `≈ ${baseSymbol}${baseEquivalent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseCode}${rateText}`;
  } else if (baseCode !== 'USD') {
    const usdCurr = currencies?.find(c => c.code?.toUpperCase() === 'USD');
    const usdRate = parseFloat(usdCurr?.exchangeRate);
    let usdEquivalent = numAmount;
    if (usdRate && !isNaN(usdRate) && usdRate > 0) {
      usdEquivalent = usdRate > 1 ? (numAmount / usdRate) : (numAmount * usdRate);
    }
    tooltipText = `≈ $${usdEquivalent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD • Base: ${baseCode}`;
  } else {
    const secondaryCurr = currencies?.find(c => c.code?.toUpperCase() !== 'USD' && !c.isBaseCurrency && parseFloat(c.exchangeRate) > 0);
    if (secondaryCurr) {
      const secRate = parseFloat(secondaryCurr.exchangeRate);
      const secSym = secondaryCurr.symbol || secondaryCurr.code;
      const secCode = secondaryCurr.code.toUpperCase();
      const secEquivalent = numAmount * secRate;
      tooltipText = `≈ ${secSym} ${secEquivalent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${secCode} • Base: USD`;
    } else {
      tooltipText = `Base Currency: US Dollar (USD)`;
    }
  }

  return (
    <Tooltip title={tooltipText} arrow placement="top" enterDelay={50}>
      <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor', display: 'inline-block' }}>
        {content}
      </span>
    </Tooltip>
  );
};

export default CurrencyDisplay;
